import { useScrollspy } from './useScrollspy'
import { smoothScrollToElement } from '@/utils/smoothScroll'
import { useState, useRef, useEffect, useCallback } from 'react'

interface UseScrollspyWithManualControlOptions {
  /**
   * Array of element IDs to observe
   */
  items: string[]
  /**
   * Offset from the top of the viewport (in pixels)
   */
  offset?: number
  /**
   * Timeout duration before re-enabling scrollspy after manual scroll (in ms)
   * Default: 1500
   */
  scrollTimeout?: number
  /**
   * Additional callback to run when an item is clicked
   */
  onItemClick?: (itemId: string) => void
}

/**
 * Custom hook that combines scrollspy functionality with manual scroll control.
 * Prevents flickering by pausing scrollspy updates during manual scrolling.
 *
 * @param options - Configuration options
 * @returns Object containing activeItemId and handleItemClick function
 *
 * @example
 * ```tsx
 * const { activeItemId, handleItemClick } = useScrollspyWithManualControl({
 *   items: ['section1', 'section2', 'section3'],
 *   offset: 100,
 * })
 *
 * // In your render:
 * <button onClick={() => handleItemClick('section1')} className={activeItemId === 'section1' ? 'active' : ''}>
 *   Section 1
 * </button>
 * ```
 */
export function useScrollspyWithManualControl({
  items,
  offset = 0,
  scrollTimeout = 1500,
  onItemClick,
}: UseScrollspyWithManualControlOptions) {
  const [isManualScrolling, setIsManualScrolling] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const ignoreScrollspyRef = useRef(false)
  const manuallyClickedItemRef = useRef<string | null>(null)

  // Use scrollspy hook to detect active section
  const activeItemId = useScrollspy({
    items,
    offset,
    isPaused: isManualScrolling,
  })

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  /**
   * Handles clicking on an item to scroll to it
   */
  const handleItemClick = useCallback(
    (itemId: string) => {
      // Clear any existing timeout to prevent race conditions
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }

      // Set ignore flag to prevent scrollspy from overwriting during transition
      ignoreScrollspyRef.current = true
      setIsManualScrolling(true)

      // Store the manually clicked item to show as active during transition
      manuallyClickedItemRef.current = itemId

      // Smooth scroll to the element
      smoothScrollToElement(itemId, offset)

      // Update URL hash without causing page jump
      window.history.pushState(null, '', `#${itemId}`)

      // Call optional callback
      if (onItemClick) {
        onItemClick(itemId)
      }

      // Resume scrollspy after scroll completes
      timeoutRef.current = setTimeout(() => {
        setIsManualScrolling(false)
        // Small delay before allowing scrollspy updates again
        setTimeout(() => {
          ignoreScrollspyRef.current = false
          manuallyClickedItemRef.current = null
        }, 100)
        timeoutRef.current = null
      }, scrollTimeout)
    },
    [offset, scrollTimeout, onItemClick],
  )

  /**
   * Returns the active item ID, considering manual scroll state
   * During manual scroll transition, returns the manually clicked item instead of scrollspy result
   */
  const getActiveItemId = useCallback(() => {
    // If currently in manual scroll transition, return the clicked item
    if (ignoreScrollspyRef.current && manuallyClickedItemRef.current) {
      return manuallyClickedItemRef.current
    }
    // Otherwise return the scrollspy detected item
    return activeItemId
  }, [activeItemId])

  return {
    activeItemId: getActiveItemId(),
    handleItemClick,
    isManualScrolling,
  }
}
